import React, { useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Icon,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { LoadingState } from "../LoadingState/LoadingState";
interface QRPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (success: boolean) => void;
  totalAmount: number;
  paymentMethod: "card" | "benefitpay";
  paymentUrl: string | null; // can be null at first
  paid: boolean;
}

export const QRPaymentDialog: React.FC<QRPaymentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  totalAmount,
  paymentMethod,
  paymentUrl,
  paid,
}) => {
  const theme = useTheme();
  const [paymentStatus, setPaymentStatus] = useState<
    "pending" | "success" | "failed"
  >("pending");

  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    console.log("oops I did it again");
    if (paid && !hasConfirmedRef.current) {
      setPaymentStatus("success");
      hasConfirmedRef.current = true;

      const timer = setTimeout(() => {
        onConfirm(true);
      }, 1500);

      return () => clearTimeout(timer);
    }

    // Reset ref if paid becomes false again (optional)
    if (!paid) {
      hasConfirmedRef.current = false;
    }

    // Return undefined for all other code paths
    return undefined;
  }, [paid, onConfirm]);

  return (
    <Dialog open={open} onClose={onClose}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#1E8078",
          py: 3,
          px: 3,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Icon sx={{ fontSize: 28 }}>qr_code</Icon>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Scan to Pay
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={4}>
          {/* Amount Display */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha("#1E8078", 0.03),
              borderRadius: 3,
              border: `1px solid ${alpha("#1E8078", 0.1)}`,
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: alpha(theme.palette.text.primary, 0.6),
                mb: 1,
                fontWeight: 500,
              }}
            >
              Amount Due
            </Typography>
            <Typography
              variant="h3"
              sx={{ color: "#1E8078", fontWeight: 700, mb: 2 }}
            >
              BHD {totalAmount.toFixed(3)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: alpha(theme.palette.text.primary, 0.6),
              }}
            >
              <Icon sx={{ fontSize: 20 }}>info</Icon>
              <Typography variant="body2">
                Customer should scan to complete payment
              </Typography>
            </Box>
          </Paper>

          {/* QR Code or Loader */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center", // Center vertically (in column)
              justifyContent: "center", // Center horizontally
              gap: 2,
              color: alpha(theme.palette.text.primary, 0.6),
              textAlign: "center",
              height: "100%", // Ensures vertical centering works well in parent container
              width: "100%", // Optional: fill parent width
              minHeight: "200px", // Ensure enough space even if parent is small
            }}
          >
            {paymentUrl ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <QRCodeSVG value={paymentUrl} size={256} />
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {paymentUrl}
                </Typography>
              </Box>
            ) : (
              <CircularProgress />
            )}
          </Box>

          {/* Status Indicator */}
          <Box textAlign="center">
            {paymentStatus === "pending" && (
              <LoadingState message="pending payment" />
            )}

            {paymentStatus === "success" && (
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "green",
                  fontSize: "1.1rem",
                }}
              >
                ✅ Payment Successful!
              </Typography>
            )}

            {paymentStatus === "failed" && (
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "red",
                  fontSize: "1.1rem",
                }}
              >
                ❌ Payment Timed Out
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.5),
          borderTop: `1px solid ${alpha("#FF0000", 0.1)}`,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: alpha("#FF0000", 0.3),
            color: "#FF0000",
            "&:hover": {
              backgroundColor: alpha("#FF0000", 0.05),
              borderColor: "#FF0000",
            },
          }}
        >
          Skip Monitoring
        </Button>
      </Box>
    </Dialog>
  );
};
